import type {
  AppointmentCustomerSourceRef,
  AppointmentPatientRef,
  AppointmentRecord,
} from '../api/appointment.api';

const personDisplayName = (
  person: { name?: string; firstName?: string; lastName?: string }
): string => {
  if (person.name) return person.name;
  return [person.lastName, person.firstName].filter(Boolean).join(' ');
};

const customerSourceLabel = (
  source?: AppointmentCustomerSourceRef | string | null
): string => {
  if (!source) return '';
  if (typeof source === 'string') return source;
  return source.name || source.code || '';
};

/** Patient name if present, otherwise lead name. */
export const getAppointmentSubjectName = (
  appointment: Pick<AppointmentRecord, 'patient' | 'lead'>,
  fallback = ''
): string => {
  const patient =
    typeof appointment.patient === 'object' ? appointment.patient : undefined;
  if (patient) {
    const name = personDisplayName(patient);
    if (name) return name;
  }

  const lead = typeof appointment.lead === 'object' ? appointment.lead : undefined;
  if (lead) {
    const name = personDisplayName(lead);
    if (name) return name;
  }
  return fallback;
};

export const getAppointmentSubjectPhone = (
  appointment: Pick<AppointmentRecord, 'patient' | 'lead'>
): string => {
  const patient =
    typeof appointment.patient === 'object' ? (appointment.patient as AppointmentPatientRef) : undefined;
  if (patient?.phone) return patient.phone;
  const lead = typeof appointment.lead === 'object' ? appointment.lead : undefined;
  return lead?.phone || '';
};

export const getAppointmentSubjectEmail = (
  appointment: Pick<AppointmentRecord, 'patient' | 'lead'>,
  fallback = ''
): string => {
  const patient =
    typeof appointment.patient === 'object' ? (appointment.patient as AppointmentPatientRef) : undefined;
  if (patient?.email) return patient.email;
  const lead = typeof appointment.lead === 'object' ? appointment.lead : undefined;
  return lead?.email || fallback;
};

/** Prefer patient source; fall back to lead source. Empty when neither has a named source. */
export const getAppointmentCustomerSourceLabel = (
  appointment: Pick<AppointmentRecord, 'patient' | 'lead'>
): string => {
  const patient =
    typeof appointment.patient === 'object' ? (appointment.patient as AppointmentPatientRef) : undefined;
  const fromPatient = customerSourceLabel(patient?.customerSource);
  if (fromPatient) return fromPatient;

  const lead = typeof appointment.lead === 'object' ? appointment.lead : undefined;
  return customerSourceLabel(lead?.customerSource);
};

export const getAppointmentLeadId = (
  appointment: Pick<AppointmentRecord, 'lead'>
): string => {
  const lead = appointment.lead;
  if (!lead) return '';
  if (typeof lead === 'string') return lead;
  return lead._id || '';
};
